import * as React from "react";
import { ChevronsUpDown, Loader2, X, Sparkles } from "lucide-react";
import { useTRPC } from "@/trpc/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { TagSelectorVirtualizedCommand } from "./tag-selector-virtualized-command";
import { cn } from "@/lib/utils";
import { Tag } from "../types";

interface TagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  jobDescription?: string;
}

export const TagSelector = ({
  selectedTags,
  onTagsChange,
}: TagSelectorProps) => {
  const [open, setOpen] = React.useState(false);
  const [availableTags, setAvailableTags] = React.useState<Tag[]>([]);
  const [recommendedTags, setRecommendedTags] = React.useState<Tag[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const trpc = useTRPC();
  const { data: tagsData, isLoading: tagsLoading, error: tagsError } = useQuery(trpc.tag.list.queryOptions());

  React.useEffect(() => {
    if (tagsData && !tagsLoading) {
      const tags = tagsData.map((item) => ({
        value: (item as any).value || (item as any).label,
        label: (item as any).label,
      }));
      setAvailableTags(tags);

      const randomTags = [...tags].sort(() => Math.random() - 0.5).slice(0, 5);

      setRecommendedTags(randomTags);
      setIsLoading(false);
    }
  }, [tagsData, tagsLoading]);

  if (tagsError) {
    console.error("Error loading tags:", tagsError);
  }

  const handleSelect = React.useCallback(
    (currentValue: string) => {
      const tag = availableTags.find((t) => t.value === currentValue);
      const tagValue = tag ? tag.value : currentValue;

      onTagsChange(
        selectedTags.includes(tagValue)
          ? selectedTags.filter((t) => t !== tagValue)
          : [...selectedTags, tagValue]
      );
    },
    [availableTags, selectedTags, onTagsChange]
  );

  const removeTag = React.useCallback(
    (tagToRemove: string) => {
      onTagsChange(selectedTags.filter((tag) => tag !== tagToRemove));
    },
    [selectedTags, onTagsChange]
  );

  return (
    <div className="space-y-3">
      {/* Recommended Tags */}
      {recommendedTags.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              Rekommenderade taggar
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {recommendedTags.map((tag) => (
              <Badge
                key={tag.value}
                variant={
                  selectedTags.includes(tag.value) ? "default" : "outline"
                }
                className={cn(
                  "cursor-pointer transition-colors",
                  selectedTags.includes(tag.value)
                    ? "bg-primary hover:bg-primary/80"
                    : "hover:bg-primary/10"
                )}
                onClick={() => handleSelect(tag.value)}>
                {tag.label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tag Selector */}
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between min-h-[40px] h-auto">
            <div className="flex flex-wrap gap-1 py-1">
              {selectedTags.length > 0 ? (
                selectedTags.map((tag) => {
                  const tagObj = availableTags.find((t) => t.value === tag);
                  return (
                    <Badge key={tag} variant="secondary" className="mr-1">
                      {tagObj?.label || tag}
                      <button
                        type="button"
                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeTag(tag);
                        }}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })
              ) : (
                <span className="text-muted-foreground">
                  Välj eller lägg till taggar...
                </span>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">
                Laddar taggar...
              </span>
            </div>
          ) : (
            <TagSelectorVirtualizedCommand
              options={availableTags}
              selectedTags={selectedTags}
              onSelectTag={handleSelect}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TagSelector;
